import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Car, Bike, Zap, MapPin, Navigation, CheckCircle2, Power, Info, Wallet, ShieldAlert } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useLang } from "@/contexts/LanguageContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import EarningsDashboard from "@/components/EarningsDashboard";
import DriverTodayStats from "@/components/DriverTodayStats";
import DriverHeatmap from "@/components/DriverHeatmap";
import RatingModal from "@/components/RatingModal";
import RideChat from "@/components/RideChat";
import ImageUploader from "@/components/ImageUploader";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Driver() {
  const { user, setUser } = useAuth();
  const { t } = useLang();
  const [form, setForm] = useState({
    vehicle_type: user?.driver_vehicle_type || "car",
    vehicle_model: user?.driver_vehicle_model || "",
    vehicle_brand: user?.driver_vehicle_brand || "",
    vehicle_year: user?.driver_vehicle_year || "",
    vehicle_category: user?.driver_vehicle_category || "",
    vehicle_color: user?.driver_vehicle_color || "",
    vehicle_four_doors: !!user?.driver_vehicle_four_doors,
    vehicle_air_conditioning: !!user?.driver_vehicle_air_conditioning,
    plate: user?.driver_plate || "",
    driver_photo_url: user?.driver_photo_url || "",
    vehicle_photo_url: user?.driver_vehicle_photo_url || "",
    cnh_photo_url: user?.driver_cnh_url || "",
    identity_photo_url: user?.driver_identity_url || "",
    vehicle_doc_photo_url: user?.driver_vehicle_doc_url || "",
    cnh_expires_at: user?.driver_cnh_expires_at || "",
    vehicle_doc_expires_at: user?.driver_vehicle_doc_expires_at || "",
  });
  const [isDriver, setIsDriver] = useState(!!user?.is_driver);
  const [isOnline, setIsOnline] = useState(!!user?.is_online);
  const [rides, setRides] = useState([]);
  const [activeRide, setActiveRide] = useState(null);
  const [publicSettings, setPublicSettings] = useState({ withdrawal_fee_pct: 5.0 });
  const [showRating, setShowRating] = useState(false);
  const [lastCompleted, setLastCompleted] = useState(null);
  const [docsStatus, setDocsStatus] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/public`).then(r => setPublicSettings(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isDriver) { setDocsStatus(null); return; }
    axios.get(`${API}/driver/docs-status`).then(r => setDocsStatus(r.data)).catch(() => {});
  }, [isDriver, isOnline]);

  const enable = async () => {
    if (!form.vehicle_model || !form.plate) return toast.error("Preencha modelo e placa");
    const missing = [];
    if (!form.driver_photo_url) missing.push("foto do motorista");
    if (!form.vehicle_photo_url) missing.push("foto do veículo");
    if (!form.cnh_photo_url) missing.push("foto da CNH");
    if (!form.identity_photo_url) missing.push("foto do documento");
    if (!form.vehicle_doc_photo_url) missing.push("documento do veículo");
    if (missing.length) return toast.error(`Envie: ${missing.join(", ")}`);
    try {
      const res = await axios.put(`${API}/auth/driver-mode`, {
        is_driver: true,
        vehicle_type: form.vehicle_type,
        vehicle_model: form.vehicle_model,
        vehicle_brand: form.vehicle_brand,
        vehicle_year: form.vehicle_year ? Number(form.vehicle_year) : null,
        vehicle_category: form.vehicle_category,
        vehicle_color: form.vehicle_color,
        vehicle_four_doors: !!form.vehicle_four_doors,
        vehicle_air_conditioning: !!form.vehicle_air_conditioning,
        plate: form.plate,
        driver_photo_url: form.driver_photo_url,
        vehicle_photo_url: form.vehicle_photo_url,
        cnh_photo_url: form.cnh_photo_url,
        identity_photo_url: form.identity_photo_url,
        vehicle_doc_photo_url: form.vehicle_doc_photo_url,
        cnh_expires_at: form.cnh_expires_at || null,
        vehicle_doc_expires_at: form.vehicle_doc_expires_at || null,
        is_online: false,
      });
      setUser(res.data);
      setIsDriver(true);
      toast.success("Modo motorista ativado!");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const toggleOnline = async () => {
    const next = !isOnline;
    try {
      const geo = next && navigator.geolocation
        ? await new Promise((res) => navigator.geolocation.getCurrentPosition(
            (pos) => res({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
            () => res({ lat: null, lng: null }),
          ))
        : { lat: null, lng: null };
      const res = await axios.put(`${API}/auth/driver-mode`, {
        vehicle_type: form.vehicle_type,
        is_online: next,
        lat: geo.lat, lng: geo.lng,
      });
      setUser(res.data);
      setIsOnline(next);
      toast.success(next ? "Você está online!" : "Você está offline");
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const loadPending = async () => {
    if (!isDriver || !isOnline) return;
    try {
      const res = await axios.get(`${API}/rides/pending`);
      setRides(res.data);
    } catch (err) {
      // 403 = expected when driver goes offline between polls; log everything else
      if (err.response?.status !== 403) console.error("[Driver] loadPending failed", err);
    }
  };

  // Restore active accepted ride on mount
  useEffect(() => {
    if (!isDriver) return;
    (async () => {
      try {
        const res = await axios.get(`${API}/rides/driver-active`);
        if (res.data) setActiveRide(res.data);
      } catch (err) {
        console.error("[Driver] restore active ride failed", err);
      }
    })();
  }, [isDriver]);

  useEffect(() => {
    if (!isDriver || !isOnline) return;
    loadPending();
    const interval = setInterval(loadPending, 4000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDriver, isOnline]);

  const accept = async (rideId) => {
    try {
      const res = await axios.post(`${API}/rides/${rideId}/accept`);
      setActiveRide(res.data);
      toast.success("Corrida aceita!");
      loadPending();
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const reject = async (rideId) => {
    try {
      await axios.post(`${API}/rides/${rideId}/reject`, { reason: "" });
      toast.info("Corrida recusada");
      setRides(rs => rs.filter(r => r.ride_id !== rideId));
    } catch (e) { toast.error(e.response?.data?.detail || "Erro"); }
  };

  const complete = async () => {
    try {
      await axios.post(`${API}/rides/${activeRide.ride_id}/complete`);
      setLastCompleted(activeRide);
      setActiveRide(null);
      toast.success("Corrida finalizada!");
      setShowRating(true);
      loadPending();
    } catch { toast.error("Erro"); }
  };

  if (!isDriver) {
    return (
      <div className="max-w-2xl mx-auto">
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-primary flex items-center justify-center">
              <Car className="w-6 h-6 text-primary-foreground" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t("becomeDriver")}</h1>
              <p className="text-sm text-muted-foreground">Aceite corridas e ganhe dinheiro</p>
            </div>
          </div>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: "car", label: t("car"), icon: Car },
                { key: "moto", label: t("moto"), icon: Bike },
              ].map((v) => (
                <button
                  key={v.key}
                  data-testid={`driver-vehicle-${v.key}`}
                  onClick={() => setForm({ ...form, vehicle_type: v.key })}
                  className={`p-4 rounded-xl border-2 transition-all ${form.vehicle_type === v.key ? "border-primary bg-primary/5" : "border-border"}`}
                >
                  <v.icon className={`w-6 h-6 mx-auto mb-2 ${form.vehicle_type === v.key ? "text-primary" : "text-muted-foreground"}`} />
                  <div className="text-sm font-semibold">{v.label}</div>
                </button>
              ))}
            </div>
            <Input data-testid="driver-vehicle-model" placeholder={t("vehicleModel")} value={form.vehicle_model} onChange={(e) => setForm({ ...form, vehicle_model: e.target.value })} />
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="driver-vehicle-brand" placeholder="Marca (ex: Fiat)" value={form.vehicle_brand} onChange={(e) => setForm({ ...form, vehicle_brand: e.target.value })} />
              <Input data-testid="driver-vehicle-year" placeholder="Ano" type="number" min="1980" max="2030" value={form.vehicle_year} onChange={(e) => setForm({ ...form, vehicle_year: e.target.value })} />
              <Input data-testid="driver-vehicle-color" placeholder="Cor" value={form.vehicle_color} onChange={(e) => setForm({ ...form, vehicle_color: e.target.value })} />
              <Input data-testid="driver-vehicle-category" placeholder="Categoria (sedan/suv/hatch)" value={form.vehicle_category} onChange={(e) => setForm({ ...form, vehicle_category: e.target.value })} />
            </div>
            <div className="flex gap-3">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="driver-4doors" type="checkbox" checked={!!form.vehicle_four_doors} onChange={(e) => setForm({ ...form, vehicle_four_doors: e.target.checked })} className="w-4 h-4 accent-primary" />
                4 portas
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input data-testid="driver-ac" type="checkbox" checked={!!form.vehicle_air_conditioning} onChange={(e) => setForm({ ...form, vehicle_air_conditioning: e.target.checked })} className="w-4 h-4 accent-primary" />
                Ar-condicionado
              </label>
            </div>
            <Input data-testid="driver-plate" placeholder={t("plateLabel")} value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} />

            <div className="p-3 rounded-lg border border-dashed border-primary/40 bg-primary/5 space-y-3" data-testid="driver-docs-block">
              <div className="text-sm font-semibold">Documentos obrigatórios</div>
              <div className="text-[11px] text-muted-foreground -mt-1">Envie da galeria ou tire foto com a câmera. Todos os campos são obrigatórios.</div>
              <div className="grid grid-cols-1 gap-3">
                {[
                  { key: "driver_photo_url", label: "Foto do motorista", testid: "driver-photo", accept: "image/*" },
                  { key: "vehicle_photo_url", label: "Foto do veículo", testid: "driver-vehicle-photo", accept: "image/*" },
                  { key: "cnh_photo_url", label: "Foto ou PDF da CNH", testid: "driver-cnh", accept: "image/*,application/pdf" },
                  { key: "identity_photo_url", label: "Documento de identidade (RG/CPF, foto ou PDF)", testid: "driver-id", accept: "image/*,application/pdf" },
                  { key: "vehicle_doc_photo_url", label: "Documento do veículo (CRLV, foto ou PDF)", testid: "driver-vehicle-doc", accept: "image/*,application/pdf" },
                ].map((doc) => {
                  const url = form[doc.key];
                  const isPdf = url && (url.toLowerCase().endsWith(".pdf") || url.toLowerCase().includes(".pdf?"));
                  return (
                  <div key={doc.key} className="space-y-1.5">
                    <label className="text-xs text-muted-foreground font-medium">{doc.label}</label>
                    <div className="flex items-center gap-2">
                      <Input
                        data-testid={`${doc.testid}-url`}
                        placeholder="Cole a URL ou envie do celular"
                        value={form[doc.key]}
                        onChange={(e) => setForm({ ...form, [doc.key]: e.target.value })}
                        className="flex-1"
                      />
                      <ImageUploader
                        testid={`${doc.testid}-upload`}
                        label="Enviar"
                        variant="outline"
                        size="sm"
                        accept={doc.accept}
                        onUploaded={(url) => setForm((f) => ({ ...f, [doc.key]: url }))}
                      />
                    </div>
                    {url && (
                      <div className="relative w-full max-w-[240px]">
                        {isPdf ? (
                          <a
                            href={url}
                            target="_blank"
                            rel="noreferrer"
                            data-testid={`${doc.testid}-preview-pdf`}
                            className="flex items-center gap-2 p-3 rounded-md border border-border bg-muted hover:bg-muted/70 text-xs"
                          >
                            <span className="w-8 h-10 rounded bg-red-500 text-white flex items-center justify-center text-[10px] font-bold flex-shrink-0">PDF</span>
                            <span className="flex-1 truncate underline">Abrir documento</span>
                          </a>
                        ) : (
                          <img
                            src={url}
                            alt={doc.label}
                            className="rounded-md border border-border w-full h-24 object-cover"
                            data-testid={`${doc.testid}-preview`}
                          />
                        )}
                        <button
                          type="button"
                          onClick={() => setForm({ ...form, [doc.key]: "" })}
                          data-testid={`${doc.testid}-clear`}
                          className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-black/80 text-white text-xs hover:bg-black"
                          aria-label="Remover"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                  );
                })}
                <div className="grid grid-cols-2 gap-2 pt-1">
                  <div>
                    <label className="text-xs text-muted-foreground">Validade CNH</label>
                    <Input data-testid="driver-cnh-exp" type="date" value={form.cnh_expires_at} onChange={(e) => setForm({ ...form, cnh_expires_at: e.target.value })} />
                  </div>
                  <div>
                    <label className="text-xs text-muted-foreground">Validade doc. veículo</label>
                    <Input data-testid="driver-vdoc-exp" type="date" value={form.vehicle_doc_expires_at} onChange={(e) => setForm({ ...form, vehicle_doc_expires_at: e.target.value })} />
                  </div>
                </div>
              </div>
            </div>
            <Button data-testid="driver-enable-btn" onClick={enable} className="w-full rounded-full h-11">
              <Zap className="w-4 h-4 mr-2" /> Ativar modo motorista
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto space-y-6">
      {/* Docs expiry alert (feature 35) */}
      {docsStatus && (docsStatus.blocked || docsStatus.cnh?.status === "expiring_soon" || docsStatus.vehicle_doc?.status === "expiring_soon") && (
        <Card
          className={`p-4 border-2 ${docsStatus.blocked ? "border-red-500 bg-red-500/10" : "border-orange-500 bg-orange-500/10"}`}
          data-testid="driver-docs-alert"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className={`w-6 h-6 flex-shrink-0 ${docsStatus.blocked ? "text-red-500" : "text-orange-500"}`} />
            <div className="flex-1">
              <div className={`font-bold text-sm ${docsStatus.blocked ? "text-red-600" : "text-orange-600"}`} data-testid="driver-docs-alert-title">
                {docsStatus.blocked ? "Documentos vencidos" : "Documentos vencendo em breve"}
              </div>
              <div className="text-xs text-muted-foreground mt-1" data-testid="driver-docs-alert-msg">{docsStatus.message}</div>
              <div className="flex flex-wrap gap-2 mt-2 text-xs">
                {docsStatus.cnh?.status !== "valid" && (
                  <Badge
                    className={`text-[10px] ${docsStatus.cnh?.status === "expired" ? "bg-red-500 text-white" : "bg-orange-500 text-white"}`}
                    data-testid="driver-docs-cnh-badge"
                  >
                    CNH: {docsStatus.cnh?.status === "expired" ? "vencida" : docsStatus.cnh?.status === "expiring_soon" ? `vence em ${docsStatus.cnh?.days_left}d` : "não informada"}
                  </Badge>
                )}
                {docsStatus.vehicle_doc?.status !== "valid" && (
                  <Badge
                    className={`text-[10px] ${docsStatus.vehicle_doc?.status === "expired" ? "bg-red-500 text-white" : "bg-orange-500 text-white"}`}
                    data-testid="driver-docs-vehicle-badge"
                  >
                    Veículo: {docsStatus.vehicle_doc?.status === "expired" ? "vencido" : docsStatus.vehicle_doc?.status === "expiring_soon" ? `vence em ${docsStatus.vehicle_doc?.days_left}d` : "não informado"}
                  </Badge>
                )}
              </div>
            </div>
          </div>
        </Card>
      )}

      {/* Driver earnings info banner — dynamic commission from admin settings */}
      <Card className="p-5 border-green-500/40 bg-gradient-to-br from-green-500/10 via-transparent to-primary/10">
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-green-600 flex items-center justify-center flex-shrink-0">
            <Wallet className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              {(publicSettings.platform_commission_pct || 0) > 0 ? (
                <>
                  <span className="font-bold">Comissão de {publicSettings.platform_commission_pct}% por corrida</span>
                  <Badge className="bg-blue-600 text-[10px]" data-testid="driver-info-badge">PLATAFORMA {publicSettings.platform_commission_pct}%</Badge>
                </>
              ) : (
                <>
                  <span className="font-bold">100% da corrida é seu</span>
                  <Badge className="bg-green-600 text-[10px]" data-testid="driver-info-badge">SEM COMISSÃO</Badge>
                </>
              )}
            </div>
            <p className="text-sm text-muted-foreground" data-testid="driver-info-banner">
              {(publicSettings.platform_commission_pct || 0) > 0 ? (
                <>
                  A plataforma retém <strong>{publicSettings.platform_commission_pct}%</strong> de cada corrida.
                  Além disso, cobra uma <strong>taxa de saque de {publicSettings.withdrawal_fee_pct}%</strong> no momento em que você retira o saldo.
                </>
              ) : (
                <>
                  Você recebe o valor <strong>integral</strong> de cada corrida.
                  A plataforma cobra apenas uma <strong>taxa de saque de {publicSettings.withdrawal_fee_pct}%</strong> no momento em que você retira o saldo.
                </>
              )}
            </p>
          </div>
        </div>
      </Card>

      <Card className={`p-5 flex items-center justify-between ${isOnline ? "bg-green-500/10 border-green-500/30" : ""}`}>
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isOnline ? "bg-green-500" : "bg-muted"}`}>
            <Power className={`w-5 h-5 ${isOnline ? "text-white" : "text-muted-foreground"}`} />
          </div>
          <div>
            <div className="font-bold">{isOnline ? t("online") : t("offline")}</div>
            <div className="text-xs text-muted-foreground">{form.vehicle_model} · {form.plate}</div>
          </div>
        </div>
        <Switch data-testid="driver-online-toggle" checked={isOnline} onCheckedChange={toggleOnline} />
      </Card>

      <EarningsDashboard />

      <DriverTodayStats />
      <DriverHeatmap />

      {activeRide && (
        <Card className="p-5 border-primary/40 bg-primary/5 animate-pop-in">
          <Badge className="mb-3">Corrida ativa</Badge>
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={activeRide.rider_picture} />
              <AvatarFallback>{activeRide.rider_name?.[0]}</AvatarFallback>
            </Avatar>
            <div className="flex-1">
              <div className="font-bold">{activeRide.rider_name}</div>
              <div className="text-sm font-bold text-primary">R$ {activeRide.price?.toFixed(2)}</div>
            </div>
          </div>
          <div className="space-y-2 text-sm mb-4">
            <div className="flex items-start gap-2"><MapPin className="w-4 h-4 text-primary mt-0.5" /><span>{activeRide.pickup_address}</span></div>
            <div className="flex items-start gap-2"><Navigation className="w-4 h-4 text-green-600 mt-0.5" /><span>{activeRide.dest_address}</span></div>
          </div>
          <Button data-testid="driver-complete-btn" onClick={complete} className="w-full rounded-full">
            <CheckCircle2 className="w-4 h-4 mr-2" /> {t("completeRide")}
          </Button>

          {/* Real-time chat with passenger */}
          <div className="mt-4">
            <RideChat
              rideId={activeRide.ride_id}
              rideStatus={activeRide.status || "accepted"}
              otherName={activeRide.rider_name || "Passageiro"}
            />
          </div>
        </Card>
      )}

      <div>
        <h2 className="text-xl font-bold tracking-tight mb-3">{t("pendingRides")}</h2>
        {!isOnline && (
          <Card className="p-8 text-center text-muted-foreground">
            <Power className="w-10 h-10 mx-auto mb-3 opacity-30" />
            Fique online para ver corridas
          </Card>
        )}
        {isOnline && rides.length === 0 && !activeRide && (
          <Card className="p-8 text-center text-muted-foreground">{t("noPendingRides")}</Card>
        )}
        <div className="space-y-3">
          {!activeRide && isOnline && rides.map((r) => (
            <Card key={r.ride_id} className="p-4 hover-lift animate-pop-in" data-testid={`pending-ride-${r.ride_id}`}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={r.rider_picture} />
                    <AvatarFallback className="text-xs">{r.rider_name?.[0]}</AvatarFallback>
                  </Avatar>
                  <div className="text-sm font-medium">{r.rider_name}</div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-primary">R$ {r.price?.toFixed(2)}</div>
                  <div className="text-xs text-muted-foreground">{r.distance_km} km</div>
                </div>
              </div>
              <div className="space-y-1 text-sm mb-3">
                <div className="flex items-start gap-2"><MapPin className="w-3.5 h-3.5 text-primary mt-1" /><span className="truncate">{r.pickup_address}</span></div>
                <div className="flex items-start gap-2"><Navigation className="w-3.5 h-3.5 text-green-600 mt-1" /><span className="truncate">{r.dest_address}</span></div>
              </div>
              <div className="flex gap-2">
              <Button data-testid={`accept-ride-${r.ride_id}`} onClick={() => accept(r.ride_id)} className="flex-1 rounded-full">
                {t("acceptRide")}
              </Button>
              <Button data-testid={`reject-ride-${r.ride_id}`} onClick={() => reject(r.ride_id)} variant="outline" className="rounded-full">
                Recusar
              </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>

      <RatingModal
        open={showRating}
        onOpenChange={setShowRating}
        ride={lastCompleted}
        target={lastCompleted ? { name: lastCompleted.rider_name, picture: lastCompleted.rider_picture, role: "Passageiro" } : null}
      />
    </div>
  );
}
